import { useRef } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  StarIcon,
  SparklesIcon,
} from "@heroicons/react/24/solid";
import useClickOutside from "../../../hooks/useClickOutside";

type Props = {
  onClose: () => void;
};

const COMPETENCY_TIPS: { name: string; tip: string; example: string }[] = [
  {
    name: "Problem Solving",
    tip: "Give them something genuinely ambiguous and watch how they break it down, rather than asking a puzzle with one right answer.",
    example: "“How would you design a system to catch duplicate job postings?”",
  },
  {
    name: "Technical Fundamentals",
    tip: "Ask about their own past decisions instead of quizzing definitions - you learn more from how they defend a real choice.",
    example: "“Why that data structure? What would you change about it now?”",
  },
  {
    name: "Learning & Adaptability",
    tip: "Introduce a twist mid-answer and see how they adjust, rather than asking a separate hypothetical question.",
    example: "“Now assume this needs to handle 10x the traffic - what changes?”",
  },
  {
    name: "Requirements & Business Reasoning",
    tip: "Hand them a deliberately vague spec and see what they ask before jumping to a solution.",
    example: "“Build a feature to let users bookmark jobs - what would you ask before starting?”",
  },
  {
    name: "Communication",
    tip: "No special question needed - just encourage them to think out loud throughout, and notice whether you can follow their reasoning.",
    example: "“Walk me through what you're thinking as you go.”",
  },
];

/**
 * Shown once, automatically, to an interviewer who's never seen it (see
 * useInterviewGuide.ts), and reachable anytime after via the button next to Manual
 * Evaluations. Orients them to what's actually on the page first (the scorecard, the
 * transcript auto-fill) before getting into how to use either one - the how-to sections
 * won't make sense to someone who doesn't yet know those features exist.
 */
export default function InterviewGuideModal({ onClose }: Props) {
  const modalRef = useRef(null);
  useClickOutside(modalRef, onClose);

  const modalContent = (
    <div className="fixed inset-0 flex justify-center z-50 h-screen backdrop-blur-sm">
      <div
        className="relative p-2 shadow-lg md:w-[65%] w-[95%] max-h-[88%] overflow-y-auto dark:bg-gray-800 bg-gray-50 top-10 rounded-md border border-gray-300 dark:border-gray-600"
        ref={modalRef}
      >
        <div className="mx-auto px-4 pt-0 pb-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-4 flex items-center justify-between p-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Interviewing &amp; scoring &mdash; a quick guide
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Section 1 - lead with the actual trigger (marking Pass/Excellent),
              since that's the moment this is relevant to them, rather than describing
              the page in the abstract. */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 mb-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-900/30">
                  <StarIcon className="h-4 w-4 text-amber-400" />
                </span>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  If you mark a candidate <strong className="text-gray-800 dark:text-gray-100">Pass</strong> or{" "}
                  <strong className="text-gray-800 dark:text-gray-100">Excellent</strong>, you&rsquo;ll be shown a
                  <strong className="text-gray-800 dark:text-gray-100"> scorecard</strong> and you need to fill it
                  in &mdash; a 1&ndash;5 star rating for each competency, with an optional
                  note on what you observed. That&rsquo;s what actually produces the
                  grade.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/30">
                  <SparklesIcon className="h-4 w-4 text-indigo-500" />
                </span>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  Inside that scorecard, you can also paste a transcript of the
                  interview and it will draft a rating and evidence for you to review
                  and adjust &mdash; it never submits anything on its own.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2 - how to probe each competency */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <AcademicCapIcon className="h-5 w-5 text-entntblue" />
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                How to probe each competency
              </h3>
            </div>
            <div className="space-y-3">
              {COMPETENCY_TIPS.map((c) => (
                <div
                  key={c.name}
                  className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-slate-800/40 p-3"
                >
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {c.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{c.tip}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic mt-1">
                    e.g. {c.example}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3 - filling in the scorecard */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardDocumentCheckIcon className="h-5 w-5 text-entntblue" />
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                Filling in the scorecard
              </h3>
            </div>
            <ul className="list-disc list-inside space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
              <li>A star rating (1&ndash;5) is required for each competency. Evidence notes are optional, but the more specific they are, the more useful the record is later.</li>
              <li>Genuinely didn't get to something? Mark it <strong>Not observed</strong> rather than guessing - a guess is worse than a gap.</li>
            </ul>
          </div>

          {/* Section 4 - transcript quality */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <DocumentTextIcon className="h-5 w-5 text-entntblue" />
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                If you use a transcript to auto-fill the scorecard
              </h3>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
              The auto-fill only picks up what's clearly in the transcript - it can't
              read intent. A few habits make a real difference to how good the draft is:
            </p>
            <ul className="list-disc list-inside space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
              <li>State the question or problem out loud, rather than just typing it into a shared doc.</li>
              <li>Let the candidate reason through their answer verbally instead of just showing you code or a diagram silently.</li>
              <li>Avoid talking over the candidate - overlapping speech is exactly what a transcript loses first.</li>
            </ul>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-entntblue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById("root") as HTMLElement);
}
